import { call, put, select } from 'redux-saga/effects';
import { CalendarEvent } from 'shovel-lib/types';
import * as actions from './actions';
import * as semesterSelectors from '../semester/selectors';
import { apiCall } from '../common/operations';
import { applyTogether } from '../common/actions';
import { undoActions } from '../undo';
import { plannerApi as webPlannerApi } from '@api/plannerApi';
import { getErrorMessage } from '@api/network';
import { actionIdOf } from '@api/activityLogApi';
import { followBlocks } from '@utils/calendar/floatToNow';
import { clearBumpersGhosts, endBumpersPreview } from '@utils/calendar/bumpersPreview';
import { bumpersToast, movesOnlyItself, planBumpersDrop } from '@utils/calendar/bumpers';
import { alert } from '@components/common/snackbarNotifications/Notifications';
import { RootState } from '@state/types';
import { SessionPlace } from '../undo/types';
import storage from '../../utils/storage';
import { darkTheme, lightTheme } from '../../assets/themes';
import t from '../../i18n/t';

const iso = (at: number | string | Date) => new Date(at).toISOString();

type Bump = ReturnType<typeof actions.bumpSessions>['payload'];

/**
 * Saves a Bumpers drop (timer spec "Bumpers"): the dragged session and the ones it bumps are drawn in their new places
 * at once, sliding there from where they were seen, and saved in one request, all or none (`/planner/rearrange` when
 * a session was split, else `move-bulk`). Then the Undo toast. Should the save fail, they go back.
 *
 * `planned`: the dragged session was just made from a task dropped from the sidebar; undoing takes it away too.
 */
function* saveBump({ id, place, moves, pieces, message, done }: Bump, planned?: number, wereAt: SessionPlace[] = []) {
  const all = [{ id, ...place }, ...moves];
  const plannedEvents = yield select((state: RootState) => state.calendar.plannedEvents);
  const places = all.map(move => ({ id: move.id, start: iso(move.start), end: iso(move.end) }));
  // Where they were: as the store has them, or (already drawn bumped at a sidebar drop) as they were before it.
  const known = new Map(wereAt.map(place => [String(place.id), place]));
  const before = all
    .filter(move => plannedEvents[move.id])
    .map(
      move =>
        known.get(String(move.id)) || {
          id: Number(move.id),
          start: iso(plannedEvents[move.id].start),
          end: iso(plannedEvents[move.id].end)
        }
    );

  // Followed from where they are seen (the preview's places, or the dropped block where it was let go), as the store
  // draws them where they go; not sideways within a day, where only their width changes.
  followBlocks(
    all.map(move => move.id),
    { sideways: false }
  );
  yield put(actions.previewPlannedMoves(places));
  endBumpersPreview({ dropped: true });

  const semesterId = yield select(semesterSelectors.getId);
  const toSave = places.map(move => ({ id: Number(move.id), start: move.start, end: move.end }));
  const splitting = pieces.length > 0;
  const { ok, ...response } = splitting
    ? yield apiCall(webPlannerApi.rearrangeSessions, semesterId, {
        moves: toSave,
        pieces: pieces.map(piece => ({ fromId: Number(piece.fromId), start: iso(piece.start), end: iso(piece.end) }))
      })
    : yield apiCall(webPlannerApi.movePlannedTasks, semesterId, toSave);
  // The calendar's own copy of the dropped block goes once the store's is drawn: the calendar redraws the day it was
  // in from the store, and taken away at the drop, the block was gone for a moment (Petr, 2026-10-24).
  done?.();
  if (!ok) {
    clearBumpersGhosts();
    followBlocks(
      before.map(move => move.id),
      { sideways: false }
    );
    yield put(actions.previewPlannedMoves(before));
    const error = getErrorMessage(response);
    alert(t[error] || error);
    return false;
  }

  const saved = yield select((state: RootState) => state.calendar.plannedEvents);
  const moved: CalendarEvent[] = (splitting ? response.data?.moved : response.data) || [];
  const created: CalendarEvent[] = (splitting && response.data?.created) || [];
  // One redraw for the lot; the preview's copies of the pieces give way to them.
  clearBumpersGhosts();
  yield put(
    applyTogether([
      ...moved.map(newEvent => actions.resizePlannedTask.success({ oldEvent: saved[newEvent.id!], newEvent })),
      ...(created.length ? [actions.planTasks.success(created)] : [])
    ])
  );
  const createdIds = created.map(event => Number(event.id));
  // The server's id for the change, so the activity log marks it undone (actionIdOf).
  const actionId = actionIdOf(response);
  yield put(
    undoActions.record({
      entry:
        planned !== undefined
          ? {
              kind: 'planned',
              plannedSessionIds: [planned, ...createdIds],
              unplannedSessions: [],
              reviewTaskIds: [],
              places: before.filter(move => move.id !== planned),
              to: toSave.filter(move => move.id !== planned),
              actionId
            }
          : { kind: 'sessionsBumped', places: before, to: toSave, createdIds, actionId },
      message
    })
  );
  return true;
}

export function* bumpSessions({ payload }: ReturnType<typeof actions.bumpSessions>) {
  yield call(saveBump, payload);
}

/**
 * A task dropped from the sidebar with Bumpers on: once its session is made where it was dropped, it bumps the sessions
 * it landed on, as a dragged one does. Undo takes the session away and puts the others back. False when nothing else
 * had to move (or there was no room), for the plain "planned" Undo.
 */
export function* bumpNewSession(session: CalendarEvent, wereAt: SessionPlace[] = []) {
  // Worked out from where the sessions were before the drop, should they be drawn bumped already.
  const state: RootState = withPlaces(yield select(s => s), wereAt);
  const theme = storage.getTheme() === 'light' ? lightTheme : darkTheme;
  const drop = { id: String(session.id), start: new Date(session.start).getTime(), end: new Date(session.end).getTime() };
  const plan = planBumpersDrop(state, theme, drop);
  if (!plan || plan.kind === 'refused' || movesOnlyItself(plan, drop)) return false;
  const settings: any = state.settings.userSettings;
  const words = {
    droppedAt: drop.start,
    time24: !!settings?.time24Format
  };
  return yield call(
    saveBump,
    {
      id: drop.id,
      place: { start: plan.start, end: plan.end },
      moves: plan.moves.map(({ id, start, end }) => ({ id, start, end })),
      pieces: plan.creates.map(({ fromId, start, end }) => ({ fromId, start, end })),
      message: bumpersToast(plan, session.title, words, { planned: true })
    },
    Number(session.id),
    wereAt
  );
}

/** The store with these sessions where they were. */
const withPlaces = (state: RootState, places: SessionPlace[]): RootState => {
  if (!places.length) return state;
  const plannedEvents = { ...state.calendar.plannedEvents } as any;
  places.forEach(place => {
    if (plannedEvents[place.id])
      plannedEvents[place.id] = { ...plannedEvents[place.id], start: place.start, end: place.end };
  });
  return { ...state, calendar: { ...state.calendar, plannedEvents } };
};

/** Sessions drawn bumped at a sidebar drop that didn't move after all: back where they were, sliding there. */
export function* putBackBumped(places: SessionPlace[]) {
  followBlocks(
    places.map(place => place.id),
    { sideways: false }
  );
  yield put(actions.previewPlannedMoves(places));
}
